// Color space conversions.
// Blending in linear RGB; palette selection & distances in Lab.
// All internal channels use 0..1 floats. XYZ and D65 use 0..1 scale (consistent).
// References: IEC 61966-2-1 (sRGB), CIE 15:2004 (Lab)

import type { LabColor, RGBLinear, SRGB } from '../types';

// D65 illuminant reference white, 0..1 scale (Y = 1.0)
export const D65_WHITE = { X: 0.95047, Y: 1.0, Z: 1.08883 };

const delta = 6 / 29;
const delta3 = delta * delta * delta; // ~0.008856
const kappa = 24389 / 27;             // ~903.3

// sRGB-encoded (0..1) -> linear-light (0..1), per channel
export function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

// linear-light (0..1) -> sRGB-encoded (0..1), per channel. No rounding.
export function linearToSrgb(c: number): number {
  return c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

export function srgbToRgbLinear(s: SRGB): RGBLinear {
  return {
    r: srgbToLinear(s.r),
    g: srgbToLinear(s.g),
    b: srgbToLinear(s.b),
  };
}

export function rgbLinearToSrgb(rgb: RGBLinear): SRGB {
  return {
    r: linearToSrgb(clamp01(rgb.r)),
    g: linearToSrgb(clamp01(rgb.g)),
    b: linearToSrgb(clamp01(rgb.b)),
  };
}

// linear-light RGB (0..1) -> XYZ (0..1, D65-relative)
export function rgbLinearToXyz(rgb: RGBLinear): { X: number; Y: number; Z: number } {
  const r = rgb.r, g = rgb.g, b = rgb.b;
  return {
    X: r * 0.4124564 + g * 0.3575761 + b * 0.1804375,
    Y: r * 0.2126729 + g * 0.7151522 + b * 0.0721750,
    Z: r * 0.0193339 + g * 0.1191920 + b * 0.9503041,
  };
}

// XYZ (0..1, D65-relative) -> Lab
export function xyzToLab(xyz: { X: number; Y: number; Z: number }): LabColor {
  const xr = xyz.X / D65_WHITE.X;
  const yr = xyz.Y / D65_WHITE.Y;
  const zr = xyz.Z / D65_WHITE.Z;
  const fx = xr > delta3 ? Math.cbrt(xr) : xr / (3 * delta * delta) + 4 / 29;
  const fy = yr > delta3 ? Math.cbrt(yr) : yr / (3 * delta * delta) + 4 / 29;
  const fz = zr > delta3 ? Math.cbrt(zr) : zr / (3 * delta * delta) + 4 / 29;
  return {
    L: 116 * fy - 16,
    a: 500 * (fx - fy),
    b: 200 * (fy - fz),
  };
}

// Lab -> XYZ (0..1, D65-relative)
// Blockers #2: threshold L > 8 (not L > 0.08). Component-wise fx/fy/fz > delta.
export function labToXyz(lab: LabColor): { X: number; Y: number; Z: number } {
  const fy = (lab.L + 16) / 116;
  const fx = lab.a / 500 + fy;
  const fz = fy - lab.b / 200;

  const xr = fx > delta ? fx * fx * fx : 3 * delta * delta * (fx - 4 / 29);
  const yr = lab.L > 8 ? fy * fy * fy : 3 * delta * delta * (fy - 4 / 29);
  const zr = fz > delta ? fz * fz * fz : 3 * delta * delta * (fz - 4 / 29);

  return {
    X: xr * D65_WHITE.X,
    Y: yr * D65_WHITE.Y,
    Z: zr * D65_WHITE.Z,
  };
}

// RGB linear -> Lab
export function rgbLinearToLab(rgb: RGBLinear): LabColor {
  return xyzToLab(rgbLinearToXyz(rgb));
}

// Lab -> RGB linear (gamut clamped to sRGB via clamp01)
export function labToRgbLinear(lab: LabColor): RGBLinear {
  const xyz = labToXyz(lab);
  const r = xyz.X * 3.2404542 + xyz.Y * -1.5371385 + xyz.Z * -0.4985314;
  const g = xyz.X * -0.9692660 + xyz.Y * 1.8760108 + xyz.Z * 0.0415560;
  const b = xyz.X * 0.0556434 + xyz.Y * -0.2040259 + xyz.Z * 1.0572252;
  return { r: clamp01(r), g: clamp01(g), b: clamp01(b) };
}

// Lab -> sRGB (0..1, gamut clamped)
export function labToSrgb(lab: LabColor): SRGB {
  const rgb = labToRgbLinear(lab);
  return rgbLinearToSrgb(rgb);
}

// RGB linear -> Lab (alias)
export function rgbToLab(rgb: RGBLinear): LabColor {
  return rgbLinearToLab(rgb);
}

// sRGB (0..1) -> Lab
export function srgbToLab(s: SRGB): LabColor {
  return rgbLinearToLab(srgbToRgbLinear(s));
}

// Pure derivation functions for PaletteColor (blocker #4).
export function toLinearRgb(color: LabColor): RGBLinear {
  return labToRgbLinear(color);
}

export function toSrgb(color: LabColor): SRGB {
  return labToSrgb(color);
}
